/**
 * OperadExplainer — progressive-disclosure explainer for operads.
 *
 * An operad is the categorical structure of "multi-input, single-output
 * operations that compose by substitution." It is the precise statement of
 * what it means for safety to be closed under composition, which is exactly
 * how CSK's `COMPOSE` Neutralization Theorem makes unsafe assembly
 * structurally inexpressible.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, SAFE, THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  FreeOperadProgramTree,
  OperadicSubstitutionAnimation,
  OperadicTree,
  SafetyClosureTree,
} from "./operad/index.js";

const ACCENT = THEOREM_COLOR.COMPOSE;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-operad",
  kind: "Categorical structure",
  title: "Operad",
  subtitle: "P(0), P(1), P(2), … with γ : P(n) ⊗ P(k₁) ⊗ … ⊗ P(kₙ) → P(k₁+…+kₙ)",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Cooperad", href: "#explainer-cooperad" },
  { label: "Bioperad", href: "#explainer-bioperad" },
  { label: "Recursion schemes", href: "#explainer-recursion-schemes" },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "F-Algebra", href: "#explainer-falgebra" },
];

function OperadDiagramStrip(): React.JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gap: "0.75rem",
        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
      }}
    >
      <OperadicTree accent={ACCENT} />
      <OperadicSubstitutionAnimation accent={ACCENT} />
      <SafetyClosureTree accent={ACCENT} />
    </div>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      An <strong>operad</strong> is a family of operations that take many inputs and produce one
      output, together with a rule for plugging one operation into another's input slot. CSK uses
      operads to express the structural law "if every input is safe, the composite is safe" — no
      runtime check required.
    </p>
  ),
  intuition: (
    <>
      <p>
        Imagine a kitchen with a set of recipes. Each recipe has some number of ingredient slots
        (its <em>arity</em>) and produces a single dish. <strong>Substitution</strong> is the rule
        that lets you fill one of those slots with another recipe instead of a raw ingredient — for
        example, plugging "make sauce" into the "sauce" slot of "plate the pasta." Do this for every
        slot and you can build arbitrarily large recipes purely by combination.
      </p>
      <p>
        An operad is exactly that combinator: a family <MathInline>{tex`P(n)`}</MathInline> of "
        <em>n</em>-ary operations" plus a way to substitute, satisfying just enough laws —{" "}
        <strong>associativity</strong>, <strong>unit</strong>, and <strong>equivariance</strong>{" "}
        under renaming of input slots — to make every nested substitution agree. The picture is a
        tree: leaves are inputs, internal nodes are operations, the root is the final output.
      </p>
      <p>
        For CSK, the recipes are operations on a safe data type, the dish is "a safe value of that
        type," and the operad's structural law is the security guarantee: a tree of safe operations
        cannot produce an unsafe output.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        A (symmetric) <strong>operad</strong> <MathInline>{tex`P`}</MathInline> in a symmetric
        monoidal category <MathInline>{tex`(C, \otimes, I)`}</MathInline> consists of:
      </p>
      <ul>
        <li>
          For each <MathInline>{tex`n \in \mathbb{N}`}</MathInline>, an object{" "}
          <MathInline>{tex`P(n) \in C`}</MathInline> of "<em>n</em>-ary operations."
        </li>
        <li>
          A <strong>unit</strong> <MathInline>{tex`\mathrm{id} \in P(1)`}</MathInline>.
        </li>
        <li>
          A <strong>composition</strong>{" "}
          <MathBlock ariaLabel="γ from P(n) tensor P(k_1) tensor ... tensor P(k_n) to P(k_1 + ... + k_n)">
            {tex`\gamma : P(n) \otimes P(k_{1}) \otimes \cdots \otimes P(k_{n}) \to P(k_{1} + \cdots + k_{n})`}
          </MathBlock>
          that plugs <em>n</em> operations into the <em>n</em> input slots of an outer operation.
        </li>
        <li>
          An action of the symmetric group <MathInline>{tex`\Sigma_{n}`}</MathInline> on{" "}
          <MathInline>{tex`P(n)`}</MathInline> (equivariance: renaming input slots commutes with
          substitution).
        </li>
      </ul>
      <p>These data satisfy three coherence laws:</p>
      <ul>
        <li>
          <strong>Associativity.</strong> Substituting "into the substitution" agrees with
          substituting "twice in a row." The associahedra <MathInline>{tex`K_{n}`}</MathInline> are
          the precise combinatorial witnesses.
        </li>
        <li>
          <strong>Unit.</strong> Substituting <MathInline>{tex`\mathrm{id} \in P(1)`}</MathInline>{" "}
          into any slot is the identity.
        </li>
        <li>
          <strong>Equivariance.</strong> Permuting input slots before or after composing gives the
          same result.
        </li>
      </ul>
      <p>
        Equivalently, an operad is a monoid object in the category of symmetric sequences under the
        composition product. The CSK COMPOSE module presents the same structure as a monoid in a
        monoidal category — see <code>agda/Kernel/COMPOSE/MonoidalBase.agda</code>.
      </p>
      <FreeOperadProgramTree accent={ACCENT} />
      <p style={{ margin: "0.5rem 0 0" }}>
        The picture above is one element of the <strong>free operad</strong> on a signature: a tree
        whose leaves carry typed inputs and whose internal nodes carry generator names. Every
        program in a typed kernel category is one such tree.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        CSK uses operads to make the security guarantee "<strong>safe-in → safe-out</strong>" into a
        statement about <em>which trees exist</em>, not a property to verify after the fact. Given a
        carrier <MathInline>{tex`A`}</MathInline> with a safety predicate{" "}
        <MathInline>{tex`\mathrm{Safe} \subseteq A`}</MathInline>, a <strong>SafeO-Algebra</strong>{" "}
        over a binary operad has a composition law that takes two safe inputs to a safe output. By
        induction over the tree, every operation in the free operad applied to safe leaves produces
        a safe root.
      </p>
      <ul>
        <li
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.5rem",
            margin: "0.25rem 0",
            padding: "0.25rem 0.5rem",
            backgroundColor: SAFE[100],
            border: `1px solid ${SAFE[300]}`,
            borderRadius: "0.4rem",
          }}
        >
          <span style={{ fontFamily: FONT.mono, fontWeight: 700, color: SAFE[700] }}>safe</span>
          <span>
            COMPOSE neutralization theorem: a <code>COMPOSE-Violation</code> would claim two safe
            inputs yield an unsafe composite, contradicting the SafeO-Algebra's composition law. The
            violation type is therefore empty — structurally inexpressible.
          </span>
        </li>
      </ul>
      <p>
        Concrete vulnerability classes that vanish once operations are required to live in the
        operad:
      </p>
      <ul>
        <li>
          <strong>CWE-668</strong> (resource exposure to the wrong sphere): cross-sphere composition
          would violate the SafeO-Algebra's composition law.
        </li>
        <li>
          <strong>CWE-502</strong> (deserialization of untrusted data): the deserialization
          operation is not in the operad unless its predecessor is safe — there is no closed tree
          term that names the unsafe assembly.
        </li>
        <li>
          <strong>CWE-426</strong> (untrusted search path): a path-resolution operation composed
          with file access requires both to live in the operad.
        </li>
      </ul>
      <p>
        The same operad structure also organizes <strong>A∞ coherence</strong>: the Mac Lane
        pentagon is the <MathInline>{tex`K_{4}`}</MathInline> associahedron coherence law on the
        COMPOSE operad. Reparenthesizing a 4-input safe composite cannot change its safety verdict.
      </p>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — COMPOSE (C)",
      path: "docs/reference/Theorem-Canon.md",
      note: "§4.2 INERT mode: operadic closure of safety under composition (safe-in → safe-out).",
    },
    {
      label: "COMPOSE Neutralization (Agda)",
      path: "agda/Kernel/COMPOSE/Neutralization.agda",
      note: "Mechanized proof that COMPOSE-Violation is empty.",
    },
    {
      label: "Operad as monoid in a monoidal category (Agda)",
      path: "agda/Kernel/COMPOSE/MonoidalBase.agda",
      note: "BinaryOperad presented as a monoid object via the upstream monoidal record.",
    },
    {
      label: "A∞-operad coherence (Agda)",
      path: "agda/Foundation/CompositionPrinciples/OperadCoherence.agda",
      note: "Mac Lane pentagon and the Stasheff associahedra as COMPOSE coherence shapes.",
    },
    {
      label: "Path operad (Agda MathLib)",
      path: "agda/MathLib/DifferentialGeometry/PathOperad.agda",
      note: "Concrete operad of composable paths used downstream of COMPOSE.",
    },
  ],
};

export function OperadExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<OperadDiagramStrip />}
      crossLinks={CROSS_LINKS}
    />
  );
}
